import React, { useState, useEffect } from "react";
import { Box, TextField, Paper, Button, Grid, Typography, IconButton, CircularProgress } from "@mui/material";
import { Add, Close, Delete, Edit, Search } from "@mui/icons-material";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import DynamicTable from "../../../../components/lab-dynamic-table";
import {
  getDefaultRecordDeleteTooltip,
  getDefaultRecordEditTooltip,
  isDefaultLabRecord,
} from "../../utils/defaultRecordActions";
import DeleteConfirmationModal from "../../../../components/Modals/delete-modal";

const ResultTemplate = () => {
  const [templateName, setTemplateName] = useState("");
  const [description, setDescription] = useState("");
  const [resultValues, setResultValues] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [tableLoading, setTableLoading] = useState(false);
  const [editData, setEditData] = useState<any>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [total, setTotal] = useState(0);
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [templatesData, setTemplatesData] = useState<any[]>([]);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedItemId, setSelectedItemId] = useState("");
  const [errors, setErrors] = useState({
    templateName: "",
    resultValues: "",
  });

  const API_ROOT = process.env.REACT_APP_LAB_URL;

  // Debounce search input
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedSearch(searchInput), 500);
    return () => clearTimeout(handler);
  }, [searchInput]);

  // Fetch templates when component mounts or dependencies change
  useEffect(() => {
    fetchTemplates();
  }, [page, rowsPerPage, debouncedSearch]);

  const fetchTemplates = async () => {
    try {
      setTableLoading(true);
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${API_ROOT}/result-template/getAllByPagination`,
        {
          params: {
            count: rowsPerPage,
            pageNo: page + 1,
            search: debouncedSearch,
          },
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response?.data?.isSuccess) {
        setTemplatesData(response.data.data || []);
        setTotal(response.data.totalCount || 0);
      }
    } catch (err: any) {
      console.error(err);
      toast.error(err?.message || "Error fetching templates");
    } finally {
      setTableLoading(false);
    }
  };

  const validateForm = () => {
    const newErr = { templateName: "", resultValues: "" };
    let isValid = true;

    if (!templateName.trim()) {
      newErr.templateName = "Template name is required";
      isValid = false;
    }

    if (resultValues.length === 0 || resultValues.every(val => !val.trim())) {
      newErr.resultValues = "At least one result value is required";
      isValid = false;
    }

    setErrors(newErr);
    return isValid;
  };

  const handleAddResultValue = () => {
    setResultValues([...resultValues, ""]);
  };

  const handleRemoveResultValue = (index: number) => {
    if (resultValues.length > 1) {
      setResultValues(resultValues.filter((_, i) => i !== index));
    }
  };

  const handleResultValueChange = (index: number, value: string) => {
    const updated = [...resultValues];
    updated[index] = value;
    setResultValues(updated);
    if (errors.resultValues) {
      setErrors(prev => ({ ...prev, resultValues: "" }));
    }
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const payload = {
        templateName,
        description,
        resultValues: resultValues.filter(val => val.trim())
      };

      if (editData) {
        await axios.patch(
          `${API_ROOT}/result-template/updateById/${editData._id}`,
          payload,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );
        toast.success("Template updated successfully");
      } else {
        await axios.post(
          `${API_ROOT}/result-template/addResultTemplate`,
          payload,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );
        toast.success("Template saved successfully");
      }

      // Reset form and refresh table
      resetForm();
      fetchTemplates();

    } catch (err: any) {
      console.error(err);
      toast.error(editData ? err.message : err?.message || "Error saving template");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setTemplateName("");
    setDescription("");
    setResultValues([]);
    setEditData(null);
    setErrors({ templateName: "", resultValues: "" });
  };

  const handleEditClick = (item: any) => {
    setEditData(item);
    setTemplateName(item.templateName);
    setDescription(item.description);
    setResultValues(item.resultValues);
  };

  const handleDeleteClick = (id: string) => {
    setSelectedItemId(id);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      const token = localStorage.getItem("token");
      await axios.delete(
        `${API_ROOT}/result-template/deleteById/${selectedItemId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      toast.success("Template deleted successfully");
      fetchTemplates();
      resetForm();
    } catch (err: any) {
      console.error(err);
      toast.error(err?.message || "Error deleting template");
    } finally {
      setIsDeleteModalOpen(false);
    }
  };

  return (
    <Box sx={{ px: 3, py: 2 }}>
      <ToastContainer />


      <Grid container spacing={1}>
        <Grid item xs={12} sm={4}>
          <Paper sx={{ p: 2 }}>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              <TextField
                label="Template Name"
                value={templateName}
                className="custom-input"
                size="small"
                sx={{
                  "& .MuiInputLabel-root": {
                    top: "-10px",
                  },
                  "& .MuiInputLabel-shrink": {
                    top: "0px",
                  },
                }}
                onChange={(e) => {
                  setTemplateName(e.target.value);
                  if (errors.templateName) {
                    setErrors(prev => ({ ...prev, templateName: "" }));
                  }
                }}
                fullWidth
                error={!!errors.templateName}
                helperText={errors.templateName}
              />

              <TextField
                label="Description"
                value={description}
                className="custom-input"
                size="small"
                sx={{
                  "& .MuiInputLabel-root": {
                    top: "-10px",
                  },
                  "& .MuiInputLabel-shrink": {
                    top: "0px",
                  },
                }}
                onChange={(e) => setDescription(e.target.value)}
                fullWidth
              />

              <Box>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                  <Typography variant="subtitle2">Result Values</Typography>
                  <IconButton
                    size="small"
                    onClick={handleAddResultValue}
                  >
                    <Add />
                  </IconButton>
                </Box>

                <Box
                  sx={{
                    maxHeight: "200px",
                    overflowY: "auto",
                    pr: 1, // padding right for scrollbar
                    "&::-webkit-scrollbar": {
                      width: "6px",
                    },
                    "&::-webkit-scrollbar-track": {
                      backgroundColor: "#f1f1f1",
                      borderRadius: "3px",
                    },
                    "&::-webkit-scrollbar-thumb": {
                      backgroundColor: "#888",
                      borderRadius: "3px",
                    },
                    "&::-webkit-scrollbar-thumb:hover": {
                      backgroundColor: "#555",
                    },
                  }}
                >
                  {resultValues.map((value, index) => (
                    <Box key={index} sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                      <TextField
                        value={value}
                        placeholder={`Result value ${index + 1}`}
                        className="custom-input"
                        size="small"
                        sx={{
                          "& .MuiInputLabel-root": {
                            top: "-10px",
                          },
                          "& .MuiInputLabel-shrink": {
                            top: "0px",
                          },
                        }}
                        onChange={(e) => handleResultValueChange(index, e.target.value)}
                        fullWidth
                      />
                      <IconButton
                        color="error"
                        onClick={() => handleRemoveResultValue(index)}
                        disabled={resultValues.length === 1}
                        size="small"
                      >
                        <Close />
                      </IconButton>
                    </Box>
                  ))}
                </Box>

                {errors.resultValues && (
                  <Typography color="error" variant="caption" display="block">
                    {errors.resultValues}
                  </Typography>
                )}
              </Box>

              <Button
                variant="contained"
                onClick={handleSave}
                disabled={loading || !templateName.trim() || resultValues.length === 0}
                startIcon={loading ? <CircularProgress size={18} /> : null}
                fullWidth
              >
                {editData ? "Update" : "Save"}
              </Button>

              {editData && (
                <Button
                  variant="outlined"
                  onClick={resetForm}
                  fullWidth
                >
                  Cancel
                </Button>
              )}
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} sm={8}>
          <DynamicTable
            headers={[
              "SN",
              "Template Name",
              "Description",
              "Result Values",
              "Actions",
            ]}
            data={templatesData?.map((item: any, index: number) => [
              page * rowsPerPage + index + 1,
              item.templateName || "",
              item.description || "",
              item.resultValues?.join(", ") || "",
            ])}
            loading={tableLoading}
            page={page}
            rowsPerPage={rowsPerPage}
            handleChangePage={(_e, newPage) => setPage(newPage)}
            handleChangeRowsPerPage={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            totalRows={total}
            actionButtons={[
              {
                icon: <Edit sx={{ color: "#FFB800", cursor: "pointer" }} />,
                onClick: (rowIndex: any) =>
                  handleEditClick(templatesData[rowIndex]),
                disabled: (rowIndex) => isDefaultLabRecord(templatesData[rowIndex]),
                tooltip: (rowIndex) =>
                  getDefaultRecordEditTooltip(templatesData[rowIndex]),
              },
              {
                icon: (
                  <Delete sx={{ color: "#FF5454", cursor: "pointer" }} />
                ),
                onClick: (rowIndex: any) =>
                  handleDeleteClick(templatesData[rowIndex]?._id),
                disabled: (rowIndex) => isDefaultLabRecord(templatesData[rowIndex]),
                tooltip: (rowIndex) =>
                  getDefaultRecordDeleteTooltip(templatesData[rowIndex]),
              },
            ]}
            height={"60vh"}
          />
          {templatesData?.length === 0 && !tableLoading && (
            <h1
              style={{
                color: "gray",
                marginTop: "8px",
                textAlign: "center",
                fontSize: "12px",
                fontWeight: "bold",
                marginBottom: "8px",
              }}
            >
              No data found
            </h1>
          )}
        </Grid>
      </Grid>

      <DeleteConfirmationModal
        open={isDeleteModalOpen}
        handleClose={() => setIsDeleteModalOpen(false)}
        handleConfirm={handleDeleteConfirm}
      />
    </Box>
  );
};

export default ResultTemplate;
